'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSiteGuidedToursEditor } from "@/app/features/sites/guided-tours/contexts/GuidedToursEditor";
import { GuidedTourTileEditable } from "@/app/features/sites/guided-tours/types/SiteGuidedTours.schema";
import Grid from "@mui/material/Grid2";
import {
    GuidedToursTileCardFrame,
    GuidedToursTilesFrame
} from "@/app/features/sites/guided-tours/ui/shared/GuidedToursViewFrames";
import { Box, Stack } from "@mui/material";
import styles from "@/app/features/sites/guided-tours/ui/public/guided-tours-public.module.css";
import EditableTypographyV2 from "@/app/features/shared/editable-typography/EditableTypographyV2";
import EditableMarkdownV2 from "@/app/features/shared/editable-markdown/EditableMarkdownV2";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import { mediaRefUrl } from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import clsx from "clsx";

type GuidedTourTileUpdate = Omit<Partial<GuidedTourTileEditable>, 'id'> & { id: string };

type TileErrors = {
    title?: string;
    subtitle?: string;
    description?: string;
};

export default function GuidedToursTilesEditable() {
    const { editable, updateEditable, errorForField } = useSiteGuidedToursEditor();
    const toursRef = useRef(editable?.tours ?? []);

    const tours = useMemo(() =>
            [...editable?.tours ?? []]
                .sort((a, b) => a.order - b.order),
        [editable?.tours]
    );

    useEffect(() => {
        toursRef.current = editable?.tours ?? [];
    }, [editable?.tours]);

    const updateTile = useCallback((partialTile: GuidedTourTileUpdate) => {
        updateEditable({
            tours: toursRef.current.map((tour) =>
                tour.id === partialTile.id ? { ...tour, ...partialTile } : tour
            )
        });
    }, [updateEditable]);

    if (!editable) return null;

    return (
        <GuidedToursTilesFrame>
            {tours.map((tile, index) => (
                <Grid key={tile.id} size={1}>
                    <TileItem
                        tile={tile}
                        onUpdate={updateTile}
                        errors={{
                            title: errorForField?.(`tours.${index}.title`) || undefined,
                            subtitle: errorForField?.(`tours.${index}.subtitle`) || undefined,
                            description: errorForField?.(`tours.${index}.description`) || undefined,
                        }}
                    />
                </Grid>
            ))}
        </GuidedToursTilesFrame>
    );
}

const TileItem = memo(({
    tile,
    onUpdate,
    errors,
}: {
    tile: GuidedTourTileEditable;
    onUpdate: (partialTile: GuidedTourTileUpdate) => void;
    errors: TileErrors;
}) => {
    const [isEditingDescription, setIsEditingDescription] = useState(false);

    const updateTitle = useCallback((title: string) => {
        onUpdate({
            id: tile.id,
            title,
        });
    }, [tile.id, onUpdate]);

    const updateSubtitle = useCallback((subtitle: string) => {
        onUpdate({
            id: tile.id,
            subtitle,
        });
    }, [tile.id, onUpdate]);

    const updateDescription = useCallback((description: string | undefined) => {
        if (description === undefined) return;
        onUpdate({
            id: tile.id,
            description,
        });
    }, [tile.id, onUpdate]);

    return (
        <GuidedToursTileCardFrame animated={false} elevated={Boolean(tile.exhibition)}>
            <Stack spacing={2} className="flex flex-auto">
                <TileMedia src={tile.image} />
                <Stack spacing={2} className="px-4 pb-2">
                    <EditableTypographyV2
                        label="Titel"
                        variant="h2"
                        color="primary"
                        className={styles.tileTitle}
                        value={tile.title}
                        onChange={updateTitle}
                        error={Boolean(errors.title)}
                        helperText={errors.title}
                    />
                    <EditableTypographyV2
                        label="Untertitel"
                        variant="subtitle1"
                        component="h3"
                        color="secondary"
                        className={styles.tileTitle}
                        value={tile.subtitle ?? ''}
                        onChange={updateSubtitle}
                        error={Boolean(errors.subtitle)}
                        helperText={errors.subtitle}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            height: { xs: "auto", md: 200 },
                            minHeight: { md: 200 },
                            maxHeight: { md: 200 },
                            minWidth: 0,
                            overflow: "hidden",
                        }}
                    >
                        <EditableMarkdownV2
                            label="Beschreibung"
                            value={tile.description}
                            onChange={updateDescription}
                            onEditingChange={setIsEditingDescription}
                            typographyOverrides={{ paragraph: "body2" }}
                            error={Boolean(errors.description)}
                            helperText={errors.description}
                            ariaLabel="Kachelbeschreibung bearbeiten"
                            fitToAvailableHeightOnEdit={isEditingDescription}
                            slots={{
                                root: {
                                    sx: {
                                        display: "flex",
                                        height: "100%",
                                        maxHeight: "100%",
                                        width: "100%",
                                        minHeight: 0,
                                        minWidth: 0,
                                        "& > [role='button']": {
                                            boxSizing: "border-box",
                                            display: "flex",
                                            flexDirection: "column",
                                            height: "100%",
                                            maxHeight: "100%",
                                            width: "100%",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                        },
                                    },
                                },
                                markdown: {
                                    root: {
                                        sx: {
                                            height: "100%",
                                            maxHeight: "100%",
                                            minHeight: 0,
                                            overflow: "hidden",
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                </Stack>
            </Stack>
        </GuidedToursTileCardFrame>
    );
}, (prev, next) =>
    prev.tile === next.tile
    && prev.errors.title === next.errors.title
    && prev.errors.subtitle === next.errors.subtitle
    && prev.errors.description === next.errors.description
);
TileItem.displayName = "TileItem";

const TileMedia = memo(
    function TileMedia({
        src,
    }: {
        src: MediaRef;
    }) {
        return (
            <MediaView
                src={src}
                fill
                showLoadingSpinner={false}
                slots={tileMediaSlots}
            />
        );
    },
    (prev, next) => mediaRefUrl(prev.src) === mediaRefUrl(next.src)
);

const tileMediaSlots = {
    root: {
        className: styles.tileImageFrame,
    },
    image: {
        className: clsx('signature-border', styles.tileImage),
        quality: 75,
        loading: 'lazy' as const,
        sizes: '(max-width: calc(var(--content-max-width) / 3)) 100vw, 33vw',
        style: {
            objectFit: 'cover' as const,
        },
    },
};
